import { ComponentType, FC, ReactNode, memo } from 'react';
import { FiCheck, FiChevronLeft } from 'react-icons/fi';
import { Sparkles } from 'lucide-react';
import './onboarding-chips.css';

export const PRIMARY_BUTTON_CLASS = 'w-full !h-12 !rounded-xl font-bold !border-transparent shadow-lg shadow-purple-light/25 hover:brightness-110 disabled:!bg-purple disabled:!opacity-40 disabled:shadow-none';

/** Labeled step-back control. The purple wash lives on `.step-back` so modals cannot strip it. */
export const STEP_BACK_CLASS = 'step-back -ml-1 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-sm font-semibold text-gray-500 transition-colors';

/** Last slice of a scrolling step eases into the action row instead of ending on a hard edge. */
export const STEP_SCROLL_FADE_CLASS = '[mask-image:linear-gradient(to_bottom,#000_calc(100%-1.5rem),transparent)] [-webkit-mask-image:linear-gradient(to_bottom,#000_calc(100%-1.5rem),transparent)]';

/** Action row stays on the card's glass, so the button area follows the card's rounded corners. */
export const StepActionBar: FC<{ children: ReactNode }> = ({ children }) => (
    <div className="relative shrink-0 px-4 pb-4 pt-1 sm:px-8">
        {children}
    </div>
);

/** Step content scrolls. The action stays at the bottom of the card and never covers the choices. Top padding lives on the step chrome so a sticky Back row can sit flush with the card. */
export const StepFrame: FC<{ children: ReactNode; footer?: ReactNode; compact?: boolean; className?: string }> = ({ children, footer, className }) => (
    <div className={`flex max-h-full min-h-0 flex-col${className ? ` ${className}` : ''}`}>
        <div id="onboarding-step-body" className={`flow-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 sm:px-8 ${STEP_SCROLL_FADE_CLASS}`}>
            {children}
        </div>
        {footer ? <StepActionBar>{footer}</StepActionBar> : null}
    </div>
);

export const StepFooter: FC<{ children: ReactNode }> = ({ children }) => (
    <div className="shrink-0">{children}</div>
);

/** Floats above the footer button. Out of flow, so showing or hiding it does not change the step's scroll height. */
export const FooterNote: FC<{ children: ReactNode; tone?: 'hint' | 'error' }> = ({ children, tone = 'hint' }) => (
    <p
        role={tone === 'error' ? 'alert' : undefined}
        className="pointer-events-none absolute inset-x-0 bottom-full z-10 flex justify-center px-4 pb-1.5 sm:px-8"
    >
        <span
            className={`max-w-full rounded-md bg-white/95 px-2 py-0.5 text-center backdrop-blur-sm ${
                tone === 'error' ? 'text-sm text-red-500' : 'text-xs text-gray-400'
            }`}
        >
            {children}
        </span>
    </p>
);

export const INPUT_CLASS = 'w-full h-11 pl-10 pr-3 text-base border border-gray-200 rounded-xl focus:outline-none focus:border-purple-light focus:ring-2 focus:ring-purple-light/20';

export const FIELD_LABEL_CLASS = 'block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2';

export const FIELD_HINT_CLASS = 'mt-1.5 text-xs text-gray-400';

interface StepChromeProps {
    stepNumber: number;
    stepTotal: number;
    onBack?: () => void;
    trailing?: ReactNode;
    progressLabel?: string;
    hideProgress?: boolean;
}

/** Back, trailing action, and progress. Kept out of the swapping step body so those controls are not recreated on every page. */
export const StepChrome: FC<StepChromeProps> = ({
    stepNumber,
    stepTotal,
    onBack,
    trailing,
    progressLabel = 'Onboarding progress',
    hideProgress = false,
}) => {
    if (!onBack && !trailing && hideProgress) return null;
    return (
        <>
            {(onBack || trailing) && (
                <div className={`flex min-h-[24px] items-center ${hideProgress ? '' : 'mb-0.5'}`}>
                    {onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            className={STEP_BACK_CLASS}
                        >
                            <FiChevronLeft className="h-4 w-4" />
                            Back
                        </button>
                    )}
                    {trailing ? <div className="ml-auto flex shrink-0">{trailing}</div> : null}
                </div>
            )}
            {!hideProgress && (
                <div
                    className="flex gap-1.5"
                    role="progressbar"
                    aria-valuemin={1}
                    aria-valuemax={stepTotal}
                    aria-valuenow={stepNumber}
                    aria-label={progressLabel}
                >
                    {Array.from({ length: stepTotal }, (_, index) => (
                        <span
                            key={index}
                            className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                                index < stepNumber ? 'bg-gradient-lightpurple-purple-horiz' : 'bg-gray-100'
                            }`}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

export const StepTitle: FC<{ title: ReactNode; subtitle?: string }> = ({ title, subtitle }) => (
    <div className="mb-2.5 sm:mb-4">
        <h2 className="text-xl sm:text-[28px] leading-tight font-extrabold text-gray-900 text-center">{title}</h2>
        {subtitle && <p className="whitespace-pre-line text-sm sm:text-base text-gray-500 text-center mt-1 sm:mt-2">{subtitle}</p>}
    </div>
);

interface StepHeaderProps extends StepChromeProps {
    compact?: boolean;
    title: ReactNode;
    subtitle?: string;
}

export const StepHeader: FC<StepHeaderProps> = ({
    stepNumber,
    stepTotal,
    title,
    subtitle,
    onBack,
    trailing,
    progressLabel,
    hideProgress = false,
}) => {
    const showChrome = Boolean(onBack) || Boolean(trailing) || !hideProgress;
    return (
        <>
            {showChrome && (
                <div className="sticky top-0 z-20 -mx-4 bg-white/95 px-4 pb-2 pt-1.5 backdrop-blur-sm sm:-mx-8 sm:px-8 sm:pb-3 sm:pt-2">
                    <StepChrome
                        stepNumber={stepNumber}
                        stepTotal={stepTotal}
                        onBack={onBack}
                        trailing={trailing}
                        progressLabel={progressLabel}
                        hideProgress={hideProgress}
                    />
                </div>
            )}
            <StepTitle title={title} subtitle={subtitle} />
        </>
    );
};

interface ChipProps {
    label: string;
    selected: boolean;
    onToggle: () => void;
    disabled?: boolean;
    /** Selected chips can show a corner remove mark when their row is hovered. */
    removable?: boolean;
    /** A sparkle marks a chip the product chose, such as a tag matched from the pin. */
    suggested?: boolean;
}

export function chipClassName(selected: boolean, disabled = false): string {
    return [
        'taste-chip inline-flex items-center whitespace-nowrap px-3.5 py-2 rounded-full text-sm font-semibold border transition-all duration-150',
        selected
            ? 'bg-purple-light border-purple-light text-white shadow-sm shadow-purple-light/30'
            : 'bg-white border-gray-200 text-gray-700 hover:border-purple-light hover:text-purple-light',
        disabled ? 'opacity-40 cursor-not-allowed hover:border-gray-200 hover:text-gray-700' : '',
    ].filter(Boolean).join(' ');
}

/** A skill shown on a profile. Shared skills on someone else's profile are coral; the rest match the purple picked chip. */
export function profileSkillChipClassName(shared: boolean): string {
    if (shared) {
        return 'taste-chip skill-chip-shared inline-flex items-center whitespace-nowrap px-3.5 py-2 rounded-full text-sm font-semibold border text-white cursor-pointer';
    }
    return `${chipClassName(true)} cursor-pointer`;
}

export const Chip = memo(function Chip({ label, selected, onToggle, disabled = false, removable = false, suggested = false }: ChipProps) {
    const showRemove = removable && selected;
    const showSuggested = suggested && selected;
    return (
    <button
        type="button"
        aria-pressed={selected}
        aria-label={showRemove ? `Remove ${showSuggested ? 'suggested ' : ''}${label}` : undefined}
        disabled={disabled}
        onClick={onToggle}
        className={`${chipClassName(selected, disabled)}${showRemove ? ' relative' : ''}${showSuggested ? ' gap-1.5' : ''}`}
    >
        {showSuggested && (
            <Sparkles className="chip-suggest-spark" size={13} strokeWidth={2.25} aria-hidden />
        )}
        {label}
        {showRemove && (
            <span className="chip-remove-mark" aria-hidden>
                <svg className="chip-remove-glyph" viewBox="0 0 10 10" fill="none">
                    <path d="M2.2 2.2l5.6 5.6M7.8 2.2l-5.6 5.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
            </span>
        )}
    </button>
    );
});

interface ChoiceCardProps {
    label: string;
    description: string;
    Icon: ComponentType<{ className?: string }>;
    selected: boolean;
    onSelect: () => void;
}

export const ChoiceCard = memo(function ChoiceCard({ label, description, Icon, selected, onSelect }: ChoiceCardProps) {
    return (
    <button
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className={`flow-choice-card relative flex items-start gap-3 w-full text-left px-4 py-3 rounded-xl border-2 transition-all duration-150 ${
            selected
                ? 'border-purple-light bg-purple-light/5 shadow-md shadow-purple-light/10'
                : 'border-gray-100 bg-white hover:border-purple-light/40 hover:shadow-sm'
        }`}
    >
        <span
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
                selected ? 'bg-purple-light text-white' : 'bg-purple-light/10 text-purple-light'
            }`}
        >
            <Icon className="h-5 w-5" />
        </span>
        <span className="flex-1 min-w-0 pr-6">
            <span className="block font-bold text-gray-900">{label}</span>
            <span className="block text-sm text-gray-500 leading-snug mt-0.5">{description}</span>
        </span>
        {selected && (
            <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-purple-light text-white">
                <FiCheck className="h-3 w-3" aria-hidden />
            </span>
        )}
    </button>
    );
});

export const SectionLabel: FC<{ children: ReactNode; hint?: string; note?: string; action?: ReactNode; center?: ReactNode }> = ({ children, hint, note, action, center }) => (
    <div className={center
        ? 'mb-2.5 grid grid-cols-[1fr_auto_1fr] items-center gap-3'
        : `mb-2.5 flex justify-between gap-3 ${action ? 'items-center' : 'items-baseline'}`}>
        <div className={`flex min-w-0 ${action ? 'items-center gap-1.5' : 'items-baseline gap-2'}`}>
            <h3 className="shrink-0 text-sm font-bold uppercase tracking-wide text-gray-700">{children}</h3>
            {action}
            {note && <span className="truncate text-xs font-medium text-gray-400">{note}</span>}
        </div>
        {center}
        {hint && <span className={`shrink-0 text-xs font-medium text-gray-400 ${center ? 'justify-self-end' : ''}`}>{hint}</span>}
    </div>
);
